import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="login-title">
            Welcome back
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">
            Sign in to your account to continue
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="h-12"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/reset-password" className="text-sm text-emerald-700 hover:text-emerald-800 dark:text-emerald-400" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="h-12 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-800" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
          
          <p className="text-center mt-8 text-slate-600 dark:text-slate-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 font-medium" data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
      
      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-emerald-700 p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-3xl font-bold mb-4">Manage your business conversations with ease</h2>
          <p className="text-emerald-100 text-lg">
            {process.env.REACT_APP_APP_NAME || 'Flowteras'} brings all your WhatsApp conversations into one unified platform with AI-powered assistance.
          </p>
          <div className="mt-8 p-6 bg-white/10 rounded-2xl backdrop-blur">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <span className="font-bold">JD</span>
              </div>
              <div>
                <p className="font-medium">John Davis</p>
                <p className="text-sm text-emerald-200">CEO, TechStart Inc.</p>
              </div>
            </div>
            <p className="text-emerald-100">
              "{process.env.REACT_APP_APP_NAME || 'Flowteras'} transformed how we handle customer support. Response times dropped by 60% and our team loves the AI suggestions."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
